/**
 * PendingInvitationsBanner (AC-890)
 *
 * Displays pending team invitations at the top of MyTeamsPage.
 * Each invitation shows the team name, inviter username, and
 * accept/decline action buttons.
 *
 * Hidden when there are no pending invitations.
 */

import React from 'react';
import { Mail, Check, X } from 'lucide-react';
import { useTeamStore } from '../../store/teamStore';
import { TeamInvitation } from '../../types';

interface PendingInvitationsBannerProps {
  invitations: TeamInvitation[];
}

const PendingInvitationsBanner: React.FC<PendingInvitationsBannerProps> = ({
  invitations,
}) => {
  const { acceptInvitation, declineInvitation } = useTeamStore();

  if (invitations.length === 0) {
    return null;
  }

  const handleAccept = async (invitationId: number) => {
    await acceptInvitation(invitationId);
  };

  const handleDecline = async (invitationId: number) => {
    await declineInvitation(invitationId);
  };

  return (
    <div className="mb-6 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-blue-800 dark:text-blue-200">
          Pending Invitations ({invitations.length})
        </h2>
      </div>

      <div className="space-y-3">
        {invitations.map((invitation) => (
          <div
            key={invitation.invitation_id}
            className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-md p-3 border border-blue-100 dark:border-blue-900"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {invitation.team_name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Invited by {invitation.inviter_username}
              </p>
            </div>

            <div className="flex items-center gap-2 ml-3 flex-shrink-0">
              <button
                onClick={() => handleAccept(invitation.invitation_id)}
                aria-label={`Accept invitation to ${invitation.team_name}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
              >
                <Check className="w-3.5 h-3.5" aria-hidden="true" />
                Accept
              </button>
              <button
                onClick={() => handleDecline(invitation.invitation_id)}
                aria-label={`Decline invitation to ${invitation.team_name}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                <X className="w-3.5 h-3.5" aria-hidden="true" />
                Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PendingInvitationsBanner;
